import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import formatTimestamp from '../../../i18n/formatTimestamp';
import moment from 'moment';

import { Button, Card, Combobox, Input } from '@salesforce/design-system-react';

import { DateRange } from '../../../common/components/';
import rangeOptionValues from '../../../common/constants/dateRangeOptionValues';
import * as StringUtils from '../../../utilities/stringUtils';

import './AnalyticsFilter.scss';
import * as Util from '../../../common/utilities';

const LOCAL_STORAGE_KEY = 'ecs.analytics.defaultRange';

class AnalyticsFilter extends Component {
  constructor(props) {
    super(props);
    this.selectionForFilterBy = this.selectionForFilterBy.bind(this);
    this.selectionForFilter = this.selectionForFilter.bind(this);
    this.selectionForBlock = this.selectionForBlock.bind(this);
    this.selectionForJob = this.selectionForJob.bind(this);

    this.startDate = undefined;
    this.endDate = undefined;
    this.jobDateRange = null;
    this.jobKey = 0;
  }

  componentDidUpdate(prevProps) {
    // user hasn't yet selected a filterBy criteria and there is at least one asset class
    // that can be selected, so go ahead and select it
    if (!this.props.filter.filterBy && this.props.assets.data.types.length) {
      this.props.actions.modifyAnalyticsFilter({
        ...this.props.filter,
        filterBy: 'assetClass',
        filter: this.props.assets.data.types[0].type
      });

      // filter by changed, load the first of the appropriate filterBy type
    } else if (Util.propChanged(prevProps, this.props, 'filter.filterBy')) {
      const newFilter = {};
      if (this.props.filter.filterBy === 'assetClass') {
        if (this.props.assets.data.types.length) {
          newFilter.filter = this.props.assets.data.types[0].type;
        }
      } else if (this.props.filter.filterBy === 'jobId') {
        if (this.props.jobs.length) {
          newFilter.filter = this.props.jobs[0];
        }
      } else if (this.props.filter.filterBy === 'assetAttribute') {
        if (this.props.assets.data.keys.length) {
          newFilter.filter = this.props.assets.data.keys[0];
        }
      }

      this.props.actions.modifyAnalyticsFilter({
        ...this.props.filter,
        ...newFilter
      });

      // jobs were loaded, select the first one if nothing has been selected yet
    } else if (Util.propChanged(prevProps, this.props, 'jobsLoading', false)) {
      if (
        this.props.filter.filterBy === 'jobId' &&
        !this.props.filter.filter &&
        this.props.jobs.length
      ) {
        this.props.actions.modifyAnalyticsFilter({
          ...this.props.filter,
          filter: this.props.jobs[0]
        });
      }
      // the filter changed, if we're selecting by jobId, load the job blocks or select first
    } else if (Util.propChanged(prevProps, this.props, 'filter.filter')) {
      if (this.props.filter.filterBy === 'jobId' && this.props.filter.filter) {
        const selectedJob = this.props.filter.filter;
        const job = this.props.jobs.find(j => j.id === selectedJob.id);
        if (job.blocks) {
          this.selectFirstBlock(selectedJob, job.blocks, this.props);
        } else {
          // details aren't found, try to load them
          this.props.actions.getJobDetails(job);
        }
      }
    } else if (
      Util.propChanged(prevProps, this.props, 'jobDetailsLoading', false)
    ) {
      // job details have finished loading and there is no block selected, select the first block
      if (this.props.filter.filterBy === 'jobId' && !this.props.filter.block) {
        const selectedJob = this.props.filter.filter;
        const job = this.props.jobs.find(j => j.id === selectedJob.id);
        this.selectFirstBlock(selectedJob, job?.blocks, this.props);
      }
    }
  }

  selectFirstBlock(job, blocks, props) {
    if (blocks?.length) {
      if (job.send_date) {
        this.startDate = moment(job.send_date)
          .startOf('day')
          .toDate();
        this.endDate = moment(job.send_date)
          .startOf('day')
          .toDate();
        this.jobKey = job.id;

        this.jobDateRange = {
          dates: {
            startDate: this.startDate,
            endDate: this.endDate
          },
          selectedRangeOption: rangeOptionValues.CUSTOM
        };
      }

      if (job) {
        props.actions.modifyAnalyticsFilter({
          ...props.filter,
          ...this.jobDateRange?.dates,
          block: blocks[0]
        });
      }
    }
  }

  /**
   * FilterBy (assetClass or jobId)
   */
  selectionForFilterBy(filterBy) {
    const { formatMessage } = this.props.intl;
    const i18nKey = 'components.ecs.analytics.filter';
    return {
      id: filterBy,
      label: formatMessage({ id: `${i18nKey}.${filterBy}` }),
      value: filterBy
    };
  }

  filterByOptions() {
    let options = ['assetClass', 'jobId', 'assetAttribute'];

    return options.map(this.selectionForFilterBy);
  }

  selectedFilterBy() {
    if (this.props.filter.filterBy) {
      return [this.selectionForFilterBy(this.props.filter.filterBy)];
    }
    return [];
  }

  handleFilterBySelection(evt, data) {
    const update = {
      filterBy: data.selection && data.selection[0] && data.selection[0].value
    };
    if (update.filterBy !== this.props.filter.filterBy) {
      update.filter = null;
      update.block = null;
    }
    this.props.actions.modifyAnalyticsFilter({
      ...this.props.filter,
      ...update
    });
    this.props.actions.modifyAttributeFilters({});
  }

  /**
   * Filter (actual assetClasses or job_ids depending on filterBy)
   */
  selectionForFilter(option) {
    return {
      id: option,
      label: option,
      value: option
    };
  }

  selectionForJob(job) {
    const label = job.email_name ? job.email_name : job.id;
    return {
      id: job.id,
      label,
      email_name: job.email_name,
      value: job.id,
      job
    };
  }

  selectionForEctTags() {
    const label = this.props.intl.formatMessage({
      id: 'components.ecs.assetBrowser.ect_tags'
    });
    return {
      id: 'ect_tags',
      label: label,
      value: 'ect_tags'
    };
  }

  filterOptions() {
    if (this.props.filter.filterBy === 'assetClass') {
      return this.props.assets.data.types
        .map(t => t.type)
        .sort((a, b) => a.localeCompare(b))
        .map(this.selectionForFilter);
    } else if (this.props.filter.filterBy === 'jobId') {
      return this.props.jobs.map(this.selectionForJob);
    } else if (this.props.filter.filterBy === 'assetAttribute') {
      const values = this.props.assets.data.keys.map(this.selectionForFilter);
      if (this.props.rules?.data?.rules?.settings?.ect) {
        return [this.selectionForEctTags(), ...values].sort(
          StringUtils.sortByField('label', this.props.intl.locale)
        );
      } else {
        return values;
      }
    } else {
      return [];
    }
  }

  selectedFilter() {
    const filterValue = this.props.filter?.filter;
    if (filterValue) {
      if (this.props.filter.filterBy === 'jobId')
        return [this.selectionForJob(filterValue)];
      if (filterValue === 'ect_tags') return [this.selectionForEctTags()];
      return [this.selectionForFilter(filterValue)];
    }
    return [];
  }

  handleFilterSelection(evt, data) {
    const selection = data.selection && data.selection[0];
    let filter = selection && selection.value;
    if (this.props.filter.filterBy === 'jobId') {
      filter = selection && selection.job;
    }
    this.props.actions.modifyAnalyticsFilter({
      ...this.props.filter,
      filter,
      block: undefined
    });
    this.props.actions.modifyAttributeFilters({});
  }

  // Accepts a custom menu item rendering function that becomes a custom component.
  // It should return a React node.
  menuItem(props) {
    const { intl } = this.props;
    const { option } = props;
    let email;
    let sendDate;
    if (option.job && option.job.email_name) {
      email = (
        <span className="job-row">
          <span className="job-label">
            <FormattedMessage id="components.ecs.analytics.filter.labels.emailName" />
          </span>
          <span className="job-value">{option.job.email_name}</span>
        </span>
      );
    }
    if (option.job && option.job.send_date) {
      const timestamp = formatTimestamp(intl)(option.job.send_date);
      sendDate = (
        <span className="job-row">
          <span className="job-label">
            <FormattedMessage id="components.ecs.analytics.filter.labels.sendDate" />
          </span>
          <span className="job-value">{timestamp}</span>
        </span>
      );
    }

    const jobId = (
      <span className="job-row">
        <span className="job-label">
          <FormattedMessage id="components.ecs.analytics.filter.labels.jobId" />
        </span>
        <span className="job-value">{option.job.id}</span>
      </span>
    );

    return (
      <div className="job-menu-item">
        {email}
        {sendDate}
        {jobId}
      </div>
    );
  }

  /**
   * Block (block_id within job -- but showing asset class of that block)
   */
  selectionForBlock(block) {
    return {
      id: block.block_id,
      label: block.asset_type,
      value: block.block_id
    };
  }

  blockOptions() {
    if (this.props.filter?.filter?.id) {
      const job = this.props.jobs.find(
        j => j.id === this.props.filter.filter.id
      );
      if (job?.blocks) {
        return job.blocks.map(this.selectionForBlock);
      }
    }
    return [];
  }

  selectedBlock() {
    if (this.props.filter.block) {
      return [this.selectionForBlock(this.props.filter.block)];
    }
    return [];
  }

  handleBlockSelection(evt, data) {
    if (this.props.filter && this.props.filter.filter) {
      if (data.selection && data.selection[0] && data.selection[0].value) {
        const block_id =
          data.selection && data.selection[0] && data.selection[0].value;
        const job = this.props.jobs.find(
          job => this.props.filter.filter.id === job.id
        );
        if (job) {
          const block = job.blocks.find(block => block.block_id === block_id);
          this.props.actions.modifyAnalyticsFilter({
            ...this.props.filter,
            block
          });
        }
      }
    }
  }

  /**
   * Date Range
   */
  handleDateRangeChange(selection) {
    if (selection.selectedDate) {
      this.props.actions.modifyAnalyticsFilter({
        ...this.props.filter,
        ...selection.selectedDate
      });
    }
  }

  renderBlockFilter() {
    const { formatMessage } = this.props.intl;
    if (
      this.props.filter.filterBy &&
      this.props.filter.filterBy === 'jobId' &&
      this.props.filter.filter
    ) {
      const hasSpinner = this.props.jobDetailsLoading;
      const input = <Input hasSpinner={hasSpinner} />;

      return (
        <div className="block-filter">
          <Combobox
            classNameContainer="block-combobox"
            labels={{
              label: formatMessage({
                id: 'components.ecs.analytics.filter.block'
              })
            }}
            hasInputSpinner={hasSpinner}
            input={input}
            selection={this.selectedBlock()}
            options={this.blockOptions()}
            events={{
              onSelect: this.handleBlockSelection.bind(this)
            }}
            variant="readonly"
          />
        </div>
      );
    }
  }

  renderFilter() {
    const menuRenderer =
      this.props.filter.filterBy === 'jobId' ? this.menuItem.bind(this) : null;
    const hasSpinner =
      this.props.filter.filterBy === 'jobId' &&
      this.props.jobsLoading &&
      !this.props.filter.filter;

    const input = <Input hasSpinner={hasSpinner} />;
    return (
      <Combobox
        className="combobox-group magnet-left"
        classNameContainer="filter-combobox wide"
        selection={this.selectedFilter()}
        onRenderMenuItem={menuRenderer}
        hasInputSpinner={hasSpinner}
        input={input}
        options={this.filterOptions()}
        events={{
          onSelect: this.handleFilterSelection.bind(this)
        }}
        variant="readonly"
      />
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const defaultRangeList = ['', 'today', 'last 7 days', 'last 30 days'];
    const userDefaultRange = localStorage.getItem(LOCAL_STORAGE_KEY);
    const defaultRange = userDefaultRange
      ? defaultRangeList[userDefaultRange]
      : rangeOptionValues.TODAY;

    return (
      <div className="analytics-filter">
        <Card
          heading={formatMessage({
            id: 'components.ecs.analytics.filter.title'
          })}
        >
          <div className="slds-grid">
            <div className="slds-col slds-grow-none">
              <Combobox
                className="combobox-group magnet-right"
                classNameContainer="filter-combobox"
                labels={{
                  label: formatMessage({
                    id: 'components.ecs.analytics.filter.filterBy'
                  })
                }}
                options={this.filterByOptions()}
                selection={this.selectedFilterBy()}
                events={{
                  onSelect: this.handleFilterBySelection.bind(this)
                }}
                variant="readonly"
              />
              {this.renderFilter()}
            </div>
            <div className="block-fliter-col slds-col slds-grow-none">
              {this.renderBlockFilter()}
            </div>
            <div className="slds-col slds-grow-none slds-col_bump-left">
              <DateRange
                key={`date-range-for-${this.jobKey}`}
                label={formatMessage({
                  id: 'components.ecs.analytics.filter.daterRangeLabel'
                })}
                selectedRange={
                  this.jobDateRange
                    ? this.jobDateRange.selectedRangeOption
                    : defaultRange
                }
                startDate={this.startDate ? this.startDate : undefined}
                endDate={this.endDate ? this.endDate : undefined}
                onDateRangeChange={this.handleDateRangeChange.bind(this)}
                allowDefault={true}
              />
            </div>
            <div className="slds-col slds-grow-none right-nav">
              <Link
                to={{ pathname: '/analytics/reports' }}
                className="nav-link"
              >
                <Button
                  id="daily-reports"
                  variant="neutral"
                  label={formatMessage({
                    id: 'components.ecs.reports.label'
                  })}
                />
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }
}

AnalyticsFilter.propTypes = {
  assets: PropTypes.object,
  filter: PropTypes.object,
  jobs: PropTypes.array,
  jobDetailsLoading: PropTypes.bool,
  jobsLoading: PropTypes.bool,
  actions: PropTypes.object.isRequired,
  location: PropTypes.object,
  rules: PropTypes.object.isRequired
};

export default injectIntl(AnalyticsFilter);
